import { DEFAULT_TIA_WORK_MODE, isTiaWorkMode, PlcBrand, PlcLanguage, type PlcTargetProfile, type TiaWorkMode } from '../types';
import { getItem, removeItem, setItem } from './scopedSettingsStorage';

const PLC_BRAND_STORAGE_KEY = 'plc_brand';
const PLC_LANGUAGE_STORAGE_KEY = 'plc_language';
const PLC_TARGET_PROFILE_STORAGE_KEY = 'plc_target_profile';
const LEGACY_SOFTWARE_VERSION_STORAGE_KEY = 'software_version';
const LEGACY_PLC_MODEL_STORAGE_KEY = 'plc_model';
const TIA_WORK_MODE_STORAGE_KEY = 'tia_work_mode';

function isValidPlcBrand(value: string | null): value is PlcBrand {
  return Boolean(value && Object.values(PlcBrand).includes(value as PlcBrand));
}

function isValidPlcLanguage(value: string | null): value is PlcLanguage {
  return Boolean(value && Object.values(PlcLanguage).includes(value as PlcLanguage));
}

export function getSavedPlcBrand(): PlcBrand {
  const savedBrand = getItem(PLC_BRAND_STORAGE_KEY);
  return isValidPlcBrand(savedBrand) ? savedBrand : PlcBrand.Codesys;
}

export function getSavedPlcLanguage(): PlcLanguage {
  const savedLanguage = getItem(PLC_LANGUAGE_STORAGE_KEY);
  return isValidPlcLanguage(savedLanguage) ? savedLanguage : PlcLanguage.ST;
}

export function savePlcBrand(brand: PlcBrand): void {
  setItem(PLC_BRAND_STORAGE_KEY, brand);
  savePlcTargetProfile({ ...getSavedPlcTargetProfile(), brand });
}

export function savePlcLanguage(language: PlcLanguage): void {
  setItem(PLC_LANGUAGE_STORAGE_KEY, language);
}

function trimOptional(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function readStoredTargetProfile(): Partial<PlcTargetProfile> {
  const raw = getItem(PLC_TARGET_PROFILE_STORAGE_KEY);
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw) as Partial<PlcTargetProfile>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function getSavedPlcTargetProfile(): PlcTargetProfile {
  const stored = readStoredTargetProfile();
  const brand = isValidPlcBrand(stored.brand || null)
    ? stored.brand
    : getSavedPlcBrand();

  return {
    brand,
    softwareVersion: trimOptional(stored.softwareVersion) || trimOptional(getItem(LEGACY_SOFTWARE_VERSION_STORAGE_KEY)),
    hardwareSeries: trimOptional(stored.hardwareSeries),
    plcModel: trimOptional(stored.plcModel) || trimOptional(getItem(LEGACY_PLC_MODEL_STORAGE_KEY)),
    firmwareVersion: trimOptional(stored.firmwareVersion),
  };
}

export function savePlcTargetProfile(profile: PlcTargetProfile): void {
  const normalized: PlcTargetProfile = {
    brand: profile.brand,
    softwareVersion: trimOptional(profile.softwareVersion),
    hardwareSeries: trimOptional(profile.hardwareSeries),
    plcModel: trimOptional(profile.plcModel),
    firmwareVersion: trimOptional(profile.firmwareVersion),
  };

  setItem(PLC_BRAND_STORAGE_KEY, normalized.brand);
  setItem(PLC_TARGET_PROFILE_STORAGE_KEY, JSON.stringify(normalized));

  if (normalized.softwareVersion !== undefined) {
    setItem(LEGACY_SOFTWARE_VERSION_STORAGE_KEY, normalized.softwareVersion);
  } else {
    removeItem(LEGACY_SOFTWARE_VERSION_STORAGE_KEY);
  }
  if (normalized.plcModel !== undefined) {
    setItem(LEGACY_PLC_MODEL_STORAGE_KEY, normalized.plcModel);
  } else {
    removeItem(LEGACY_PLC_MODEL_STORAGE_KEY);
  }
}

/**
 * How the Agent validates and creates TIA projects (TIA 根治 P2, E12): in its
 * own headless Portal, or in a TIA window of its own. Every turn sends it; a
 * value this build does not know reads as the default.
 */
export function getSavedTiaWorkMode(): TiaWorkMode {
  const saved = getItem(TIA_WORK_MODE_STORAGE_KEY);
  return isTiaWorkMode(saved) ? saved : DEFAULT_TIA_WORK_MODE;
}

export function saveTiaWorkMode(mode: TiaWorkMode): void {
  setItem(TIA_WORK_MODE_STORAGE_KEY, mode);
}
